import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Target,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Save,
  Check,
  Search,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { generateMockReceipts, generateMockTransactions } from "@/data/payments-mock-data";
import { Receipt, Transaction, ReceiptAllocation, AllocationValidation, PromiseToPay, PromisedTransaction, Allocation, AllocationStrategy } from "@/types/payments";
import { toast } from "sonner";

// Phase 2 Components
import { AutoAllocateDropdown } from "./auto-allocate-dropdown";
import { AutoAllocatePreviewModal } from "./auto-allocate-preview-modal";
import { PromiseAlertBanner } from "./promise-alert-banner";
import { PromiseTransactionBadge } from "./promise-transaction-badge";
import { AllocationWarningDialog } from "./allocation-warning-dialog";
import { showAllocationSuccessToast } from "./allocation-success-toast";

// Phase 2 Utilities
import {
  allocateOldestFirst,
  allocateLargestFirst,
  allocateSmallestFirst,
  allocatePromisePriority,
  allocateDueDatePriority,
} from "@/lib/payments/allocation-strategies";
import { detectPromise } from "@/lib/payments/promise-detection";
import { mockPromises } from "@/lib/payments/mock-promise-data";

export default function AllocationWorkspace() {
  const { receiptId } = useParams<{ receiptId: string }>();
  const navigate = useNavigate();
  
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [allocations, setAllocations] = useState<Map<string, number>>(new Map());
  const [searchQuery, setSearchQuery] = useState("");
  
  // Phase 2: Promise Detection State
  const [promiseDetected, setPromiseDetected] = useState(false);
  const [detectedPromise, setDetectedPromise] = useState<PromiseToPay | null>(null);
  const [promisedTransactionsList, setPromisedTransactionsList] = useState<PromisedTransaction[]>([]);
  const [promiseIgnored, setPromiseIgnored] = useState(false);
  
  // Phase 2: Auto-Allocation State
  const [showPreview, setShowPreview] = useState(false);
  const [previewAllocations, setPreviewAllocations] = useState<Allocation[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState<AllocationStrategy>('oldest');
  
  // Phase 2: Warning Dialog State
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningType, setWarningType] = useState<'disputed' | 'unapplied' | 'promise_ignored' | null>(null);
  const [warningData, setWarningData] = useState<any>(null);

  // Load receipt and transactions
  useEffect(() => {
    const receipts = generateMockReceipts();
    const foundReceipt = receipts.find(r => r.id === receiptId);
    
    if (foundReceipt) {
      setReceipt(foundReceipt);
      const customerTransactions = generateMockTransactions(foundReceipt.customerId);
      setTransactions(customerTransactions);
    }
  }, [receiptId]);

  // Phase 2: Promise Detection on Load
  useEffect(() => {
    if (receipt && !promiseIgnored) {
      const promise = detectPromise(receipt, mockPromises);
      if (promise) {
        setPromiseDetected(true);
        setDetectedPromise(promise);
        setPromisedTransactionsList(promise.promisedTransactions || []);
      }
    }
  }, [receipt, promiseIgnored]);

  // Calculate totals
  const totalAllocated = useMemo(() => {
    return Array.from(allocations.values()).reduce((sum, amount) => sum + amount, 0);
  }, [allocations]);

  const remainingAmount = useMemo(() => {
    return receipt ? receipt.receivedAmount - totalAllocated : 0;
  }, [receipt, totalAllocated]);

  const allocationProgress = useMemo(() => {
    return receipt ? (totalAllocated / receipt.receivedAmount) * 100 : 0;
  }, [receipt, totalAllocated]);

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter(txn =>
      txn.transactionNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      txn.customerName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [transactions, searchQuery]);

  // Promised transactions
  const promisedTransactions = useMemo(() => {
    return transactions.filter(txn => txn.isPromised);
  }, [transactions]);

  // Format currency - defined before use in validation
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  // Validation
  const validation = useMemo((): AllocationValidation => {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (totalAllocated > (receipt?.receivedAmount || 0)) {
      errors.push(`Total allocated (${formatCurrency(totalAllocated)}) exceeds receipt amount (${formatCurrency(receipt?.receivedAmount || 0)})`);
    }

    allocations.forEach((amount, txnId) => {
      const transaction = transactions.find(t => t.id === txnId);
      if (transaction && amount > transaction.currentBalance) {
        errors.push(`Allocation to ${transaction.transactionNumber} (${formatCurrency(amount)}) exceeds balance (${formatCurrency(transaction.currentBalance)})`);
      }
    });

    if (remainingAmount > 0.01 && allocations.size > 0) {
      warnings.push(`Unapplied balance of ${formatCurrency(remainingAmount)} will remain`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }, [allocations, totalAllocated, remainingAmount, receipt, transactions]);

  // Handle allocation amount change
  const handleAllocationChange = (transactionId: string, value: string) => {
    const amount = parseFloat(value) || 0;
    const newAllocations = new Map(allocations);
    
    if (amount > 0) {
      newAllocations.set(transactionId, amount);
    } else {
      newAllocations.delete(transactionId);
    }
    
    setAllocations(newAllocations);
  };

  // Quick fill allocation
  const handleQuickFill = (transactionId: string) => {
    const transaction = transactions.find(t => t.id === transactionId);
    if (!transaction) return;

    const maxAmount = Math.min(transaction.currentBalance, remainingAmount);
    const newAllocations = new Map(allocations);
    newAllocations.set(transactionId, maxAmount);
    setAllocations(newAllocations);
  };

  // Remove allocation
  const handleRemoveAllocation = (transactionId: string) => {
    const newAllocations = new Map(allocations);
    newAllocations.delete(transactionId);
    setAllocations(newAllocations);
  };

  // Phase 2: Auto-Allocate Handler
  const handleAutoAllocate = (strategy: AllocationStrategy) => {
    if (!receipt) return;
    
    let calculatedAllocations: Allocation[] = [];
    const availableAmount = remainingAmount;
    
    switch (strategy) {
      case 'oldest':
        calculatedAllocations = allocateOldestFirst(transactions, availableAmount);
        break;
      case 'largest':
        calculatedAllocations = allocateLargestFirst(transactions, availableAmount);
        break;
      case 'smallest':
        calculatedAllocations = allocateSmallestFirst(transactions, availableAmount);
        break;
      case 'promise':
        calculatedAllocations = allocatePromisePriority(
          transactions,
          promisedTransactionsList,
          availableAmount
        );
        break;
      case 'due_date':
        calculatedAllocations = allocateDueDatePriority(transactions, availableAmount);
        break;
    }
    
    setSelectedStrategy(strategy);
    setPreviewAllocations(calculatedAllocations);
    
    // Check if preview is enabled
    const previewEnabled = localStorage.getItem('autoAllocatePreview') === 'true';
    
    if (previewEnabled) {
      setShowPreview(true);
    } else {
      applyAllocations(calculatedAllocations, strategy);
    }
  };

  // Phase 2: Apply Allocations
  const applyAllocations = (newAllocations: Allocation[], strategy?: string) => {
    const updatedAllocations = new Map(allocations);
    
    newAllocations.forEach(allocation => {
      const currentAmount = updatedAllocations.get(allocation.transactionId) || 0;
      updatedAllocations.set(allocation.transactionId, currentAmount + allocation.allocatedAmount);
    });
    
    setAllocations(updatedAllocations);
    setShowPreview(false);
    
    toast.success("Allocations Applied", {
      description: `${newAllocations.length} transaction${newAllocations.length > 1 ? 's' : ''} allocated using ${strategy || 'selected'} strategy`,
    });
  };

  // Phase 2: Promise-Based Allocation Handler
  const handleUsePromiseAllocation = () => {
    if (!receipt || !detectedPromise) return;
    
    const availableAmount = remainingAmount;
    const calculatedAllocations = allocatePromisePriority(
      transactions,
      promisedTransactionsList,
      availableAmount
    );
    
    applyAllocations(calculatedAllocations, 'Promise Priority');
    
    // Show info if there's remaining amount
    const totalAllocated = calculatedAllocations.reduce((sum, a) => sum + a.allocatedAmount, 0);
    const remaining = availableAmount - totalAllocated;
    
    if (remaining > 0) {
      toast.info("Promise Allocation Applied", {
        description: `${formatCurrency(remaining)} remaining after promise allocation. Use Auto-Allocate to allocate remaining amount.`,
        duration: 7000,
      });
    }
  };

  // Phase 2: Ignore Promise Handler
  const handleIgnorePromise = () => {
    setWarningType('promise_ignored');
    setWarningData({ promise: detectedPromise });
    setShowWarningDialog(true);
  };

  const confirmIgnorePromise = () => {
    setPromiseIgnored(true);
    setPromiseDetected(false);
    setShowWarningDialog(false);
    toast.info("Promise ignored", {
      description: "The promise will remain in 'Pending' status",
    });
  };

  // Save draft
  const handleSaveDraft = () => {
    if (!validation.isValid) {
      toast.error("Cannot save: " + validation.errors.join(", "));
      return;
    }
    toast.success("Draft saved successfully");
  };

  // Complete allocation
  const handleCompleteAllocation = () => {
    if (!validation.isValid) {
      toast.error("Cannot complete: " + validation.errors.join(", "));
      return;
    }

    // Check for unapplied balance warning
    if (remainingAmount > 0.01) {
      setWarningType('unapplied');
      setWarningData({ unappliedAmount: remainingAmount });
      setShowWarningDialog(true);
      return;
    }

    completeAllocationFinal();
  };

  const completeAllocationFinal = () => {
    const promiseFulfilled = promiseDetected && !promiseIgnored;
    const transactionCount = allocations.size;
    
    showAllocationSuccessToast(
      totalAllocated,
      transactionCount,
      promiseFulfilled,
      false // caseUpdated - will be true when backend integration is done
    );
    
    setTimeout(() => navigate("/payments/receipts"), 1500);
  };

  if (!receipt) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center">
          <p className="text-muted-foreground">Receipt not found</p>
          <Button onClick={() => navigate("/payments/receipts")} className="mt-4">
            Back to Receipts
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-4 p-4 md:p-6 pt-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/payments/receipts")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Allocate Receipt</h2>
          <p className="text-muted-foreground">
            {receipt.receiptNumber} - {receipt.customerName}
          </p>
        </div>
      </div>

      {/* Receipt Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Receipt Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="text-sm text-muted-foreground">Receipt Number</div>
              <div className="font-medium">{receipt.receiptNumber}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Customer</div>
              <div className="font-medium">{receipt.customerName}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Date</div>
              <div className="font-medium">{format(new Date(receipt.receiptDate), 'dd MMM yyyy')}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Reference</div>
              <div className="font-medium">{receipt.paymentReference}</div>
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Received Amount:</span>
              <span className="font-medium">{formatCurrency(receipt.receivedAmount)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Allocated:</span>
              <span className="font-medium text-green-600">{formatCurrency(totalAllocated)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Remaining:</span>
              <span className={cn(
                "font-medium",
                remainingAmount > 0 ? "text-orange-600" : "text-muted-foreground"
              )}>
                {formatCurrency(remainingAmount)}
              </span>
            </div>
            <Progress value={allocationProgress} className="h-2" />
            <div className="text-xs text-muted-foreground text-right">
              {allocationProgress.toFixed(1)}% allocated
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Phase 2: Promise Alert Banner */}
      {promiseDetected && !promiseIgnored && detectedPromise && (
        <PromiseAlertBanner
          promise={detectedPromise}
          promisedTransactions={promisedTransactionsList}
          onUsePromise={handleUsePromiseAllocation}
          onIgnore={handleIgnorePromise}
        />
      )}

      {/* Validation Messages */}
      {validation.errors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Validation Errors</AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside">
              {validation.errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {validation.warnings.length > 0 && validation.errors.length === 0 && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-900">Warnings</AlertTitle>
          <AlertDescription className="text-yellow-800">
            <ul className="list-disc list-inside">
              {validation.warnings.map((warning, idx) => (
                <li key={idx}>{warning}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Outstanding Transactions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Outstanding Transactions</CardTitle>
              <CardDescription>Select transactions to allocate payment</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* Phase 2: Auto-Allocate Dropdown */}
              <AutoAllocateDropdown
                receiptAmount={receipt.receivedAmount}
                allocatedAmount={totalAllocated}
                transactions={transactions}
                promiseDetected={promiseDetected}
                onAllocate={handleAutoAllocate}
                disabled={remainingAmount <= 0 || transactions.length === 0}
              />
              <div className="relative w-64">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead className="text-right">Allocate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No outstanding transactions found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((txn) => {
                    const promisedTx = promisedTransactionsList.find(p => p.transactionId === txn.id);
                    
                    return (
                      <TableRow key={txn.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{txn.transactionNumber}</span>
                            {promisedTx && (
                              <PromiseTransactionBadge
                                priority={promisedTx.priority}
                                promisedAmount={promisedTx.promisedAmount}
                              />
                            )}
                          </div>
                        </TableCell>
                      <TableCell>
                        {format(new Date(txn.transactionDate), 'dd/MM/yy')}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(txn.originalAmount)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(txn.currentBalance)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={txn.daysOverdue > 0 ? "destructive" : "outline"}>
                          {txn.daysOverdue > 0 ? `${txn.daysOverdue}d` : '-'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Input
                            type="number"
                            placeholder="0.00"
                            value={allocations.get(txn.id) || ''}
                            onChange={(e) => handleAllocationChange(txn.id, e.target.value)}
                            className="w-24 text-right"
                            step="0.01"
                            min="0"
                            max={Math.min(txn.currentBalance, remainingAmount + (allocations.get(txn.id) || 0))}
                          />
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => handleQuickFill(txn.id)}
                            disabled={remainingAmount <= 0 && !allocations.has(txn.id)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Current Allocations */}
      {allocations.size > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Current Allocations</CardTitle>
            <CardDescription>Review allocations before saving</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Array.from(allocations.entries()).map(([txnId, amount]) => {
                const txn = transactions.find(t => t.id === txnId);
                if (!txn) return null;

                return (
                  <div key={txnId} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium">{txn.transactionNumber}</div>
                      <div className="text-sm text-muted-foreground">
                        Balance: {formatCurrency(txn.currentBalance)}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-medium text-green-600">{formatCurrency(amount)}</div>
                        <div className="text-xs text-muted-foreground">
                          {((amount / txn.currentBalance) * 100).toFixed(1)}% of balance
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleRemoveAllocation(txnId)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={handleSaveDraft}
          disabled={allocations.size === 0}
        >
          <Save className="mr-2 h-4 w-4" />
          Save Draft
        </Button>
        <Button
          onClick={handleCompleteAllocation}
          disabled={!validation.isValid || allocations.size === 0}
        >
          <Check className="mr-2 h-4 w-4" />
          Complete Allocation
        </Button>
      </div>

      {/* Phase 2: Auto-Allocate Preview Modal */}
      <AutoAllocatePreviewModal
        open={showPreview}
        onOpenChange={setShowPreview}
        strategy={selectedStrategy}
        allocations={previewAllocations}
        totalAllocated={previewAllocations.reduce((sum, a) => sum + a.allocatedAmount, 0)}
        remainingAmount={remainingAmount - previewAllocations.reduce((sum, a) => sum + a.allocatedAmount, 0)}
        onApply={() => applyAllocations(previewAllocations, selectedStrategy)}
      />

      {/* Phase 2: Warning Dialogs */}
      <AllocationWarningDialog
        open={showWarningDialog}
        onOpenChange={setShowWarningDialog}
        type={warningType}
        data={warningData}
        onContinue={() => {
          if (warningType === 'promise_ignored') {
            confirmIgnorePromise();
          } else if (warningType === 'unapplied') {
            setShowWarningDialog(false);
            completeAllocationFinal();
          }
        }}
        onAutoAllocate={() => {
          setShowWarningDialog(false);
          handleAutoAllocate('oldest');
        }}
      />
    </div>
  );
}
